import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { Position } from '../types';
import { DEFAULT_DESKTOP_ICONS, ICON_SIZE, ICON_PADDING } from '../utils/constants';

interface IconPosition {
  id: string;
  gridPosition: Position; // Grid coordinates (col, row)
}

interface DesktopIconsContextType {
  iconPositions: IconPosition[];
  updateIconPosition: (id: string, gridPosition: Position) => void;
  updateMultipleIconPositions: (updates: { id: string; gridPosition: Position }[]) => void;
  getIconPosition: (id: string) => Position;
  getIconAtPosition: (gridPosition: Position) => string | null;
  resetPositions: () => void;
  // Selection state
  selectedIcons: Set<string>;
  selectIcon: (id: string, addToSelection?: boolean) => void;
  deselectIcon: (id: string) => void;
  selectMultiple: (ids: string[]) => void;
  clearSelection: () => void;
  isSelected: (id: string) => boolean;
}

const DesktopIconsContext = createContext<DesktopIconsContextType | null>(null);

const STORAGE_KEY = 'xp-desktop-icon-positions-v2';

// Get default positions from constants
const getDefaultPositions = (): IconPosition[] => {
  return DEFAULT_DESKTOP_ICONS.map(icon => ({
    id: icon.id,
    gridPosition: { x: icon.position.x, y: icon.position.y },
  }));
};

// Load positions from localStorage
const loadPositions = (): IconPosition[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge with defaults to handle new icons
      const defaults = getDefaultPositions();
      const savedMap = new Map<string, IconPosition>(parsed.map((p: IconPosition) => [p.id, p]));

      return defaults.map(def => {
        const saved = savedMap.get(def.id);
        return saved ? { ...def, gridPosition: saved.gridPosition } : def;
      });
    }
  } catch (e) {
    console.error('Failed to load icon positions:', e);
  }
  return getDefaultPositions();
};

// Save positions to localStorage
const savePositions = (positions: IconPosition[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch (e) {
    console.error('Failed to save icon positions:', e);
  }
};

interface DesktopIconsProviderProps {
  children: ReactNode;
}

export const DesktopIconsProvider: React.FC<DesktopIconsProviderProps> = ({ children }) => {
  const [iconPositions, setIconPositions] = useState<IconPosition[]>(loadPositions);
  const [selectedIcons, setSelectedIcons] = useState<Set<string>>(new Set());

  // Save to localStorage when positions change
  useEffect(() => {
    savePositions(iconPositions);
  }, [iconPositions]);

  const updateIconPosition = useCallback((id: string, gridPosition: Position) => {
    setIconPositions(prev => {
      const newPositions = prev.map(p =>
        p.id === id ? { ...p, gridPosition } : p
      );
      return newPositions;
    });
  }, []);

  const updateMultipleIconPositions = useCallback((updates: { id: string; gridPosition: Position }[]) => {
    setIconPositions(prev => {
      const updateMap = new Map(updates.map(u => [u.id, u.gridPosition]));
      return prev.map(p => {
        const newPos = updateMap.get(p.id);
        return newPos ? { ...p, gridPosition: newPos } : p;
      });
    });
  }, []);

  // Selection methods
  const selectIcon = useCallback((id: string, addToSelection = false) => {
    setSelectedIcons(prev => {
      if (addToSelection) {
        const newSet = new Set(prev);
        newSet.add(id);
        return newSet;
      }
      return new Set([id]);
    });
  }, []);

  const deselectIcon = useCallback((id: string) => {
    setSelectedIcons(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const selectMultiple = useCallback((ids: string[]) => {
    setSelectedIcons(new Set(ids));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIcons(new Set());
  }, []);

  const isSelected = useCallback((id: string) => {
    return selectedIcons.has(id);
  }, [selectedIcons]);

  const getIconPosition = useCallback((id: string): Position => {
    const icon = iconPositions.find(p => p.id === id);
    if (icon) {
      return icon.gridPosition;
    }
    // Return default position if not found
    const defaultIcon = DEFAULT_DESKTOP_ICONS.find(i => i.id === id);
    return defaultIcon?.position || { x: 0, y: 0 };
  }, [iconPositions]);

  const getIconAtPosition = useCallback((gridPosition: Position): string | null => {
    const icon = iconPositions.find(
      p => p.gridPosition.x === gridPosition.x && p.gridPosition.y === gridPosition.y
    );
    return icon?.id || null;
  }, [iconPositions]);

  const resetPositions = useCallback(() => {
    setIconPositions(getDefaultPositions());
  }, []);

  return (
    <DesktopIconsContext.Provider
      value={{
        iconPositions,
        updateIconPosition,
        updateMultipleIconPositions,
        getIconPosition,
        getIconAtPosition,
        resetPositions,
        selectedIcons,
        selectIcon,
        deselectIcon,
        selectMultiple,
        clearSelection,
        isSelected,
      }}
    >
      {children}
    </DesktopIconsContext.Provider>
  );
};

export const useDesktopIcons = (): DesktopIconsContextType => {
  const context = useContext(DesktopIconsContext);
  if (!context) {
    throw new Error('useDesktopIcons must be used within a DesktopIconsProvider');
  }
  return context;
};

// Helper function to convert grid position to pixel position
export const gridToPixel = (gridPosition: Position): Position => ({
  x: gridPosition.x * (ICON_SIZE + ICON_PADDING) + ICON_PADDING,
  y: gridPosition.y * (ICON_SIZE + ICON_PADDING) + ICON_PADDING,
});

// Helper function to convert pixel position to grid position
export const pixelToGrid = (pixelPosition: Position): Position => ({
  x: Math.round((pixelPosition.x - ICON_PADDING) / (ICON_SIZE + ICON_PADDING)),
  y: Math.round((pixelPosition.y - ICON_PADDING) / (ICON_SIZE + ICON_PADDING)),
});

// Calculate maximum grid dimensions based on screen size
export const getMaxGridDimensions = (): { maxCols: number; maxRows: number } => {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight - 30; // Subtract taskbar height

  const maxCols = Math.floor((screenWidth - ICON_PADDING) / (ICON_SIZE + ICON_PADDING));
  const maxRows = Math.floor((screenHeight - ICON_PADDING) / (ICON_SIZE + ICON_PADDING));

  return { maxCols, maxRows };
};
